// Small helpers for reading untrusted JSON: every function returns a value of
// the promised type or a fallback, and never throws.

export type Json = Record<string, unknown>;

export const isRecord = (value: unknown): value is Json =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const str = (value: unknown, fallback = ''): string =>
    typeof value === 'string' ? value : (typeof value === 'number' ? String(value) : fallback);

export const optStr = (value: unknown): string | undefined =>
    typeof value === 'string' && value !== '' ? value : undefined;

export const num = (value: unknown, fallback = 0): number => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
        return Number(value);
    }
    return fallback;
};

export const optNum = (value: unknown): number | undefined => {
    const parsed = num(value, Number.NaN);
    return Number.isNaN(parsed) ? undefined : parsed;
};

export const bool = (value: unknown): boolean => value === true;

// "MM:SS" or "H:MM:SS" -> seconds; a plain number is taken as seconds.
export const toSeconds = (value: unknown): number => {
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    if (typeof value === 'string') {
        const parts = value.split(':').map(Number);
        if (parts.length >= 2 && parts.every(Number.isFinite)) {
            return parts.reduce((total, part) => total * 60 + part, 0);
        }
        const asNumber = Number(value);
        return Number.isFinite(asNumber) ? asNumber : 0;
    }
    return 0;
};

// The backend sends collections either as arrays or as objects keyed by id
// (non-numeric keys such as metadata are not items). Returns [key, item] pairs.
export const collectionEntries = (data: unknown, { numericKeysOnly = true } = {}): Array<[string, unknown]> => {
    if (Array.isArray(data)) {
        return data.map((item, index) => [String(index), item]);
    }
    if (isRecord(data)) {
        return Object.entries(data).filter(([key]) => !numericKeysOnly || !Number.isNaN(Number(key)));
    }
    return [];
};

export const warnDropped = (what: string, key: string, reason: string) => {
    console.warn(`Dropped malformed ${what} "${key}": ${reason}`);
};
